/**
 * MaeCameo.tsx — R15 Builder-AU CS.4 cute layer
 *
 * Transient 2.5s overlay that shows Mae (chibi earth-chan sprite) in the
 * bottom-right corner with a speech bubble containing a 1-line SDG fact.
 * Auto-dismisses after 2500ms. Click anywhere on the cameo to dismiss early.
 *
 * Triggered by 3 sites:
 *   1. Era transition  — after GrandTransitionOverlay dismisses, mood "joyful"
 *   2. First quiz      — when pendingQuiz becomes non-null for the first time, mood "idle"
 *   3. First climate vote — when pendingClimateVote fires for the first time, mood "sad"
 *
 * VISIBLE WHEN: pendingMaeCameo !== null AND boardAnimationActive === false
 * TEST PATH:
 *   Era:          play through an era boundary → cameo appears bottom-right ~1.5s
 *                 after transition overlay disappears.
 *   Quiz:         land on Event tile → newsgirl offers quiz → cameo shows.
 *   Climate vote: play until total pollution > 100 → climate vote modal fires →
 *                 cameo shows simultaneously (z:400, below modal z:1400).
 *
 * z-index: 400 — above tile UI (100-200), below modals (1000+).
 * Size: sprite 140×210 + speech bubble to the left.
 * Does NOT block input — pointer-events only on the cameo widget, not the board.
 */

import type React from "react";
import { useEffect, useRef } from "react";
import { useGameStore } from "../../store/game-store.js";
import styles from "./MaeCameo.module.css";

const SPRITE_PATHS: Record<"idle" | "joyful" | "sad", string> = {
  idle: "/sprites/mascot/mae-idle.png",
  joyful: "/sprites/mascot/mae-joyful.png",
  sad: "/sprites/mascot/mae-sad.png",
};

const DISMISS_MS = 2500;

export function MaeCameo(): React.ReactElement | null {
  const pendingMaeCameo = useGameStore((s) => s.pendingMaeCameo);
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const dismissMaeCameo = useGameStore((s) => s.dismissMaeCameo);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss after DISMISS_MS. Re-arm whenever a new cameo is set.
  useEffect(() => {
    if (pendingMaeCameo === null) return;
    if (boardAnimationActive) return;

    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      dismissMaeCameo();
    }, DISMISS_MS);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [pendingMaeCameo, boardAnimationActive, dismissMaeCameo]);

  // Defer display while board animation is active.
  if (pendingMaeCameo === null || boardAnimationActive) return null;

  const { mood, line } = pendingMaeCameo;

  return (
    <button
      type="button"
      className={styles.cameo}
      data-testid="mae-cameo"
      onClick={dismissMaeCameo}
      aria-label="Mae cameo — click to dismiss"
    >
      {/* Speech bubble — floats to the left of the sprite */}
      <div className={styles.bubble} data-testid="mae-cameo-bubble">
        <p className={styles.bubbleLine}>{line}</p>
        <span className={styles.bubbleTail} aria-hidden="true" />
      </div>

      {/* Mae sprite */}
      <img
        src={SPRITE_PATHS[mood]}
        alt={`Mae — ${mood}`}
        className={styles.sprite}
        data-testid="mae-cameo-sprite"
        data-mood={mood}
        draggable={false}
      />
    </button>
  );
}
